import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/auth';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    subjects: 0,
    grades: 0,
    homework: 0,
    nextCourse: 0,
  });
  const [nextCourses, setNextCourses] = useState([]);
  const [lastGrades, setLastGrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Stats
      try {
        const statsRes = await api.get('/student/dashboard-stats', { headers });
        setStats(statsRes.data || {});
      } catch (e) {
        // Fallback si la route n'existe pas encore
        setStats({ subjects: 5, grades: 12, homework: 3, nextCourse: 1 });
      }

      // Prochains cours
      try {
        const coursesRes = await api.get('/student/next-courses', { headers });
        setNextCourses(Array.isArray(coursesRes.data) ? coursesRes.data.slice(0, 4) : []);
      } catch (e) {
        setNextCourses([]);
      }

      // Dernières notes
      try {
        const gradesRes = await api.get('/student/last-grades', { headers });
        setLastGrades(Array.isArray(gradesRes.data) ? gradesRes.data.slice(0, 4) : []);
      } catch (e) {
        setLastGrades([]);
      }
    } catch (err) {
      console.error('Erreur dashboard élève:', err);
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.name
    ? user.name.split(' ')[0].charAt(0).toUpperCase() + user.name.split(' ')[0].slice(1).toLowerCase()
    : 'Élève';

  const getNoteColor = (note) => {
    const n = parseFloat(note);
    if (n >= 16) return 'text-green-600';
    if (n >= 14) return 'text-blue-600';
    if (n >= 12) return 'text-yellow-600';
    if (n >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">

      {/* ═══════════ BANNIÈRE HAUT ═══════════ */}
      <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-56 bg-gradient-to-r from-[#0A0A0A] to-[#F39C12]">
        <img
          src="/images/hero-students.png"
          alt="Élève"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent"></div>

        <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center max-w-xl">
          <h1 className="text-white text-3xl lg:text-4xl font-bold">
            Bonjour {firstName} 👋
          </h1>
          <p className="text-white/95 text-base lg:text-lg mt-3">
            Bienvenue sur ton espace élève.<br />
            Apprends, progresse, réalise tes rêves !
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Chargement...</div>
      ) : (
        <>
          {/* ═══════════ 4 CARTES STATS ═══════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<BookIcon className="w-7 h-7 text-white" />}
              bg="bg-[#F39C12]"
              value={stats.subjects || 0}
              label="Matières"
              linkTo="/student/courses"
            />
            <StatCard
              icon={<NotesIcon className="w-7 h-7 text-white" />}
              bg="bg-[#3B82F6]"
              value={stats.grades || 0}
              label="Notes disponibles"
              linkTo="/student/grades"
            />
            <StatCard
              icon={<HomeworkIcon className="w-7 h-7 text-white" />}
              bg="bg-[#22C55E]"
              value={stats.homework || 0}
              label="Devoirs à faire"
              linkTo="/student/homework"
            />
            <StatCard
              icon={<ClockIcon className="w-7 h-7 text-white" />}
              bg="bg-[#A855F7]"
              value={stats.nextCourse || 0}
              label="Prochain cours"
              linkTo="/student/schedule"
            />
          </div>

          {/* ═══════════ 2 COLONNES : COURS + NOTES ═══════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

            {/* Mes prochains cours */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Mes prochains cours</h3>
                <Link to="/student/schedule" className="text-[#F39C12] hover:text-[#E67E22] text-sm font-medium">
                  Voir tous →
                </Link>
              </div>

              {nextCourses.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-3xl mb-2">📅</p>
                  <p className="text-gray-500 text-sm">Aucun cours planifié</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {nextCourses.map((course, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition">
                      <div className="w-11 h-11 rounded-lg bg-[#F39C12] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {course.time || '08:00'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-[#0A0A0A]">{course.subject || 'Matière'}</p>
                        <p className="text-xs text-gray-500">{course.day || 'Aujourd\'hui'} • Salle {course.room || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mes dernières notes */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Mes dernières notes</h3>
                <Link to="/student/grades" className="text-[#F39C12] hover:text-[#E67E22] text-sm font-medium">
                  Voir tout →
                </Link>
              </div>

              {lastGrades.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-3xl mb-2">📝</p>
                  <p className="text-gray-500 text-sm">Aucune note disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {lastGrades.map((grade, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-lg bg-[#3B82F6] flex items-center justify-center text-white font-bold flex-shrink-0">
                          <NotesIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-sm text-[#0A0A0A]">{grade.subject || 'Matière'}</p>
                          <p className="text-xs text-gray-500">{grade.type || 'Note'}</p>
                        </div>
                      </div>
                      <span className={`font-extrabold text-lg ${getNoteColor(grade.score)}`}>
                        {grade.score}/{grade.max_score || 20}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ═══════════ BANNIÈRE BAS ═══════════ */}
          <div className="bg-orange-50 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0 text-white text-2xl">
              ⭐
            </div>
            <div>
              <p className="font-bold text-[#0A0A0A] text-base">Tu es capable de grandes choses !</p>
              <p className="text-gray-600 text-sm">
                Continue tes efforts, tu vas y arriver !
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════

function StatCard({ icon, bg, value, label, linkTo }) {
  return (
    <Link
      to={linkTo}
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-[#F39C12]/30 transition-all duration-300 group"
    >
      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <p className="text-3xl font-extrabold text-[#0A0A0A]">{value}</p>
      <p className="text-sm text-gray-500 mt-2">{label}</p>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ICÔNES SVG
// ═══════════════════════════════════════════════════════════════════

const BookIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const NotesIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const HomeworkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export default StudentDashboard;